import { isOpenFinRuntime } from '../containers/OpenFin/helpers'
import type { State } from '../store/types'

/* localStorage rather than OpenFin customData: the popout's store is built
   synchronously at module load and customData is async-only. */
const HYDRATE_STORAGE_PREFIX = 'hydrate:'

export type HydrationPayload = { [K in keyof State]?: Partial<State[K]> }

export const storeHydration = (
  viewName: string,
  payload: HydrationPayload
): void => {
  try {
    localStorage.setItem(
      HYDRATE_STORAGE_PREFIX + viewName,
      JSON.stringify(payload)
    )
  } catch (err) {
    console.warn('Failed to store hydration payload in localStorage', err)
  }
}

/* Consumers type these as Date and act on it: columnDefs renders an empty maturity
   cell for anything failing `instanceof Date`, and the user-orders sort calls
   submitTime.getTime() unguarded. */
const ISO_DATE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

const reviveDates = (_key: string, value: unknown) =>
  typeof value === 'string' && ISO_DATE.test(value) ? new Date(value) : value

let consumed: HydrationPayload | undefined

/* Consumers gate on what a window actually received rather than on "is a popout" —
   most window types have no builder, so they boot cold and must behave like one. */
export const wasHydrated = () => consumed !== undefined

export const getInitialHydration = (): HydrationPayload | undefined => {
  consumed = readHydration()
  return consumed
}

const readHydration = (): HydrationPayload | undefined => {
  if (!isOpenFinRuntime()) return undefined
  try {
    const key = HYDRATE_STORAGE_PREFIX + fin.me.identity.name
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    localStorage.removeItem(key)
    const parsed: unknown = JSON.parse(raw, reviveDates)
    if (!isMergeableObject(parsed)) return undefined
    /* A payload written by an older bundle can carry a slice this one no longer has
       the shape for; a non-object one replaces its default wholesale rather than
       overlaying, leaving selectors to throw on it. */
    const slices = Object.entries(parsed).filter(([, slice]) =>
      isMergeableObject(slice)
    )
    return slices.length
      ? (Object.fromEntries(slices) as HydrationPayload)
      : undefined
  } catch (err) {
    console.warn('Failed to read hydration payload from localStorage', err)
    return undefined
  }
}

export const clearAllHydration = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(HYDRATE_STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key))
  } catch (err) {
    console.warn('Failed to clear hydration payloads from localStorage', err)
  }
}

const isMergeableObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date)

/* A payload from an older bundle can hold a value where the default has a different
   shape. Keeping the default is safer than handing it to selectors typed for the
   old one, and it is what lets deepMerge still claim to return a State. */
const replaceable = (base: unknown, override: unknown) =>
  base === undefined || (override !== null && typeof override === typeof base)

const mergeInto = (base: unknown, override: unknown): unknown => {
  if (override === undefined) return base
  if (!isMergeableObject(base) || !isMergeableObject(override)) {
    return replaceable(base, override) ? override : base
  }
  const result: Record<string, unknown> = { ...base }
  for (const key of Object.keys(override)) {
    if (override[key] === undefined) continue
    result[key] = mergeInto(result[key], override[key])
  }
  return result
}

export const deepMerge = (base: State, override: HydrationPayload): State =>
  mergeInto(base, override) as State
